<template>
  <div class="stacked-progress-bar" role="img" :aria-label="ariaLabel">
    <div
      v-for="segment in visibleSegments"
      :key="segment.key"
      class="stacked-progress-bar__segment"
      :class="`stacked-progress-bar__segment--${segment.variant ?? 'neutral'}`"
      :style="{ width: widthFor(segment.value) }"
      :title="segment.title"
    ></div>
    <div
      v-if="goal != null"
      class="stacked-progress-bar__goal-marker"
      :style="{ left: widthFor(goal) }"
      :title="goalTitle"
    ></div>
  </div>
</template>

<script setup lang="ts">
import type { StackedProgressBarSegment } from "@/components/common/stackedProgressBarTypes";
import { computed } from "vue";

const props = withDefaults(
  defineProps<{
    segments: StackedProgressBarSegment[];
    total: number;
    goal?: number;
    goalTitle?: string;
    ariaLabel?: string;
  }>(),
  { goalTitle: "Target", ariaLabel: "Stacked progress" }
);

const visibleSegments = computed(() =>
  props.segments.filter((segment) => segment.value > 0)
);

const widthFor = (value: number): string => {
  if (props.total <= 0) return "0%";
  return `${(value / props.total) * 100}%`;
};
</script>

<style scoped lang="scss">
.stacked-progress-bar {
  position: relative;
  height: 0.625rem;
  background: var(--phoenix-gray-200);
  border-radius: 999px;
  overflow: hidden;
  display: flex;
}

.stacked-progress-bar__segment {
  height: 100%;
  transition: width 0.3s;
}

.stacked-progress-bar__segment--success {
  background: var(--phoenix-success);
}

.stacked-progress-bar__segment--info {
  background: var(--phoenix-info);
}

.stacked-progress-bar__segment--warning {
  background: var(--phoenix-warning);
}

.stacked-progress-bar__segment--danger {
  background: var(--phoenix-danger);
}

.stacked-progress-bar__segment--warning-stripe {
  background: repeating-linear-gradient(
    45deg,
    var(--phoenix-warning-bg-subtle),
    var(--phoenix-warning-bg-subtle) 4px,
    var(--phoenix-warning-border-subtle) 4px,
    var(--phoenix-warning-border-subtle) 8px
  );
}

.stacked-progress-bar__segment--neutral {
  background: var(--phoenix-gray-300);
}

.stacked-progress-bar__goal-marker {
  position: absolute;
  top: -2px;
  bottom: -2px;
  width: 2px;
  background: var(--phoenix-gray-1000, #0e1a14);
  opacity: 0.55;
  z-index: 2;
}

.stacked-progress-bar__goal-marker::after {
  content: "";
  position: absolute;
  left: -3px;
  top: -4px;
  width: 8px;
  height: 4px;
  background: var(--phoenix-gray-1000, #0e1a14);
  border-radius: 1px;
  opacity: 0.85;
}
</style>
